import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useServiceItems } from '../../hooks/useServiceItems';
import {
  SERVICE_CAROUSEL_ICONS,
  SERVICE_CAROUSEL_ICON_LABELS,
} from '../../data/serviceCarouselIcons';
import { ServiceSlug } from '../../data/services';
import ScrollReveal from '../ScrollReveal/ScrollReveal';
import ServiceMockScreenshot from './ServiceMockScreenshot';
import './ServicesPreview.css';

const AUTO_ADVANCE_MS = 15000;

const ServicesPreview: React.FC = () => {
  const { t, language } = useLanguage();
  const items = useServiceItems();
  const [activeIndex, setActiveIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState<'next' | 'prev'>('next');
  const activeIndexRef = useRef(0);
  const iconTrackRef = useRef<HTMLDivElement>(null);

  const activeService = items[activeIndex];
  const prevIndex = (activeIndex - 1 + items.length) % items.length;
  const nextIndex = (activeIndex + 1) % items.length;

  const scrollActiveIconInTrack = useCallback((index: number) => {
    const track = iconTrackRef.current;
    const activeButton = track?.querySelector<HTMLButtonElement>(`[data-index="${index}"]`);
    if (!track || !activeButton) return;

    const trackRect = track.getBoundingClientRect();
    const buttonRect = activeButton.getBoundingClientRect();
    const scrollLeft =
      track.scrollLeft +
      (buttonRect.left - trackRect.left) -
      trackRect.width / 2 +
      buttonRect.width / 2;

    track.scrollTo({ left: scrollLeft, behavior: 'smooth' });
  }, []);

  const goTo = useCallback(
    (index: number, direction?: 'next' | 'prev') => {
      const normalized = (index + items.length) % items.length;
      const current = activeIndexRef.current;

      if (normalized !== current) {
        if (direction) {
          setSlideDirection(direction);
        } else {
          const forward = (normalized - current + items.length) % items.length;
          const backward = (current - normalized + items.length) % items.length;
          setSlideDirection(forward <= backward ? 'next' : 'prev');
        }
      }

      activeIndexRef.current = normalized;
      setActiveIndex(normalized);
    },
    [items.length]
  );

  const goPrev = useCallback(() => goTo(activeIndex - 1, 'prev'), [activeIndex, goTo]);
  const goNext = useCallback(() => goTo(activeIndex + 1, 'next'), [activeIndex, goTo]);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
    scrollActiveIconInTrack(activeIndex);
  }, [activeIndex, scrollActiveIconInTrack]);

  useEffect(() => {
    if (items.length <= 1) return undefined;

    const intervalId = window.setInterval(() => {
      setSlideDirection('next');
      setActiveIndex((current) => {
        const next = (current + 1) % items.length;
        activeIndexRef.current = next;
        return next;
      });
    }, AUTO_ADVANCE_MS);

    return () => window.clearInterval(intervalId);
  }, [items.length]);

  if (!activeService) return null;

  const renderPanel = (index: number, variant: 'prev' | 'active' | 'next') => {
    const service = items[index];
    if (!service) return null;

    return (
      <div
        className={`services-showcase-panel services-showcase-panel--${variant}`}
        key={`${service.slug}-${variant}`}
      >
        <div className="services-showcase-frame">
          <ServiceMockScreenshot
            slug={service.slug as ServiceSlug}
            className="services-showcase-screenshot"
          />
        </div>
      </div>
    );
  };

  return (
    <section className="services-preview editorial-section section-shell section-shell--secondary section-after-curve" id="services">
      <div className="editorial-container">
        <ScrollReveal>
          <header className="services-preview-header">
            <h2 className="section-heading">
              {t.services.preview.titleLine1}{' '}
              <span className="section-heading-accent">{t.services.preview.titleLine2}</span>
            </h2>
            <p className="section-intro">{t.services.preview.subtitle}</p>
          </header>
        </ScrollReveal>
      </div>

      <ScrollReveal delay={80}>
        <div className="services-showcase">
          <div className="services-showcase-icons-wrap">
            <div className="services-showcase-icons" ref={iconTrackRef}>
              {items.map((service, index) => {
                const Icon = SERVICE_CAROUSEL_ICONS[service.slug as ServiceSlug];
                const shortLabel = SERVICE_CAROUSEL_ICON_LABELS[service.slug as ServiceSlug][language];

                return (
                  <button
                    key={service.slug}
                    type="button"
                    data-index={index}
                    className={`services-showcase-icon-btn ${index === activeIndex ? 'active' : ''}`}
                    onClick={() => goTo(index)}
                    aria-current={index === activeIndex ? 'true' : undefined}
                  >
                    <span className="services-showcase-icon-circle">
                      <Icon size={22} strokeWidth={1.75} />
                    </span>
                    <span className="services-showcase-icon-label">{shortLabel}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className={`services-showcase-stage services-showcase-stage--${slideDirection}`}>
            <button
              type="button"
              className="services-showcase-stage-nav services-showcase-stage-nav--prev"
              onClick={goPrev}
              aria-label={t.services.preview.carouselPrev}
            >
              <ChevronLeft size={22} />
            </button>

            <div className="services-showcase-panels">
              {renderPanel(prevIndex, 'prev')}
              {renderPanel(activeIndex, 'active')}
              {renderPanel(nextIndex, 'next')}
            </div>

            <button
              type="button"
              className="services-showcase-stage-nav services-showcase-stage-nav--next"
              onClick={goNext}
              aria-label={t.services.preview.carouselNext}
            >
              <ChevronRight size={22} />
            </button>
          </div>

          <div
            key={activeIndex}
            className={`services-showcase-info services-showcase-info--${slideDirection}`}
          >
            <h3 className="services-showcase-title">{activeService.title}</h3>
            <p className="services-showcase-meta">{activeService.meta}</p>
            <p className="services-showcase-tagline">{activeService.tagline}</p>
            <Link to={`/services/${activeService.slug}`} className="services-showcase-link">
              {t.services.preview.openService}
              <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </ScrollReveal>

      <div className="services-preview-cta">
        <ScrollReveal delay={160}>
          <Link to="/services" className="services-preview-all btn btn-primary">
            {t.services.preview.viewAll}
            <ArrowUpRight size={18} />
          </Link>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default ServicesPreview;
